import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import canAutoPlay from 'can-autoplay';
import { useConfig } from '../../config/ConfigContext';
import { supabase } from '../../lib/supabase';
import { extractVideoId } from '../../services/youtubeService';
import { metaTagsService } from '../../services/metaTagsService';
import { isMobileDevice } from '../../utils/deviceDetection';
import { generateShareUrl, logSmsPreviewDebug } from '../../utils/smsPreviewHelper';
import ShortsPlayerYouTube from './ShortsPlayerYouTube';
import ShortsNavigation from './ShortsNavigation';
import ShortsActions from './ShortsActions';
import ShortsInfo from './ShortsInfo';
import MobileDebugConsole from '../Debug/MobileDebugConsole';
import useShortsData from './hooks/useShortsData';
import useShortsSwipe from './hooks/useShortsSwipe';
import useShortsLikes from './hooks/useShortsLikes';
import '../../styles/shorts.css';

/**
 * Main TikTok-style shorts viewer component
 * Handles vertical video playback with swipe navigation and series filtering
 */
const ShortsViewer = () => {
  const { config, loading, theme } = useConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentIndex, setCurrentIndex] = useState(0); // Always start with index 0 (latest video after sorting)
  const [currentSeries, setCurrentSeries] = useState('all');
  const [isPlaying, setIsPlaying] = useState(true); // Autoplay first video by default
  const [isMuted, setIsMuted] = useState(true); // Start muted for reliable autoplay
  const [swipeDirection, setSwipeDirection] = useState(null); // For visual feedback
  const [hasInteracted, setHasInteracted] = useState(true); // Already interacted by navigating from Lectures
  const [canAutoplayUnmuted, setCanAutoplayUnmuted] = useState(false); // Track if unmuted autoplay is allowed
  const [autoplayDetected, setAutoplayDetected] = useState(false); // Track if we've checked autoplay capability
  
  // Mobile landscape control visibility states
  const [isLandscape, setIsLandscape] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [autoHideTimer, setAutoHideTimer] = useState(null);
  
  // Track initial load to prevent error flash
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Data fetching hook
  const { 
    shorts, 
    series, 
    loading: dataLoading, 
    error: dataError,
    refetch,
    loadMoreVideos,
    hasMoreVideos,
    loadingMore
  } = useShortsData(config?.google_sheet_id, currentSeries, config);

  // Likes management hook
  const { 
    likeCount, 
    hasLiked, 
    toggleLike 
  } = useShortsLikes(shorts[currentIndex]?.shortUrl);

  // Swipe and keyboard navigation hook
  const { 
    swipeHandlers,
    containerRef 
  } = useShortsSwipe({
    onSwipeUp: () => navigateToNext(),
    onSwipeDown: () => navigateToPrevious(),
    onSpacePress: () => togglePlayPause(),
    onMutePress: () => toggleMute()
  });

  // Handle first user interaction
  const handleFirstInteraction = useCallback(() => {
    if (!hasInteracted) {
      setHasInteracted(true);
      setIsPlaying(true);
    }
  }, [hasInteracted]);

  // Detect autoplay capability on component mount
  useEffect(() => {
    const detectAutoplay = async () => {
      try {
        // Check if unmuted video autoplay is allowed
        const { result: canPlayUnmuted } = await canAutoPlay.video({ timeout: 500, muted: false });
        setCanAutoplayUnmuted(canPlayUnmuted);
        setAutoplayDetected(true);
        
        console.log('Autoplay detection - Unmuted video autoplay allowed:', canPlayUnmuted);
        
        // If unmuted autoplay is allowed and user prefers unmuted, we can start unmuted
        if (canPlayUnmuted && !isMuted) {
          console.log('Browser allows unmuted autoplay - videos will play with sound');
        } else if (!canPlayUnmuted && !isMuted) {
          console.log('Browser blocks unmuted autoplay - will start muted and show unmute hint');
        }
      } catch (error) {
        console.warn('Could not detect autoplay capability:', error);
        // Default to safe behavior (assume unmuted is not allowed)
        setCanAutoplayUnmuted(false);
        setAutoplayDetected(true);
      }
    };

    detectAutoplay();
  }, []); // Run once on mount

  // Orientation detection for mobile landscape controls
  useEffect(() => {
    const checkOrientation = () => {
      const landscape = window.innerWidth > window.innerHeight;
      setIsLandscape(landscape);
      
      // Show controls when returning to portrait
      if (!landscape && isMobileDevice()) {
        setControlsVisible(true);
        if (autoHideTimer) {
          clearTimeout(autoHideTimer);
          setAutoHideTimer(null);
        }
      }
      // Hide controls when entering landscape on mobile
      else if (landscape && isMobileDevice()) {
        setControlsVisible(false);
      }
    };

    // Initial check
    checkOrientation();

    // Listen for orientation changes
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);

    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
      if (autoHideTimer) {
        clearTimeout(autoHideTimer);
      }
    };
  }, [autoHideTimer]);

  // Handle control interactions to reset auto-hide timer
  const handleControlInteraction = useCallback(() => {
    if (isLandscape && isMobileDevice() && controlsVisible) {
      console.log('Control interaction detected, resetting timer');
      
      // Clear existing timer
      if (autoHideTimer) {
        clearTimeout(autoHideTimer);
      }
      
      // Reset auto-hide timer
      const timer = setTimeout(() => {
        console.log('Auto-hiding controls after control interaction');
        setControlsVisible(false);
      }, 3000);
      setAutoHideTimer(timer);
    }
  }, [isLandscape, controlsVisible, autoHideTimer]);

  // Navigation functions
  const navigateToNext = useCallback(() => {
    handleFirstInteraction();
    console.log('Navigate to next - Current:', currentIndex, 'Total:', shorts.length);
    setSwipeDirection('up');
    setTimeout(() => setSwipeDirection(null), 300); // Clear animation
    
    if (currentIndex < shorts.length - 1) {
      setCurrentIndex(prev => prev + 1);
      
      // Check if we're near the end and should load more
      if (currentIndex >= shorts.length - 3 && hasMoreVideos && !loadingMore) {
        console.log('Near end of shorts, loading more...');
        loadMoreVideos();
      }
    } else {
      // Loop back to first video when reaching the end
      setCurrentIndex(0);
    }
  }, [currentIndex, shorts.length, hasMoreVideos, loadingMore, loadMoreVideos, handleFirstInteraction]);

  const navigateToPrevious = useCallback(() => {
    handleFirstInteraction();
    console.log('Navigate to previous - Current:', currentIndex, 'Total:', shorts.length);
    setSwipeDirection('down');
    setTimeout(() => setSwipeDirection(null), 300); // Clear animation
    
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    } else {
      // Loop to last video when at the beginning
      setCurrentIndex(shorts.length - 1);
    }
  }, [currentIndex, shorts.length, handleFirstInteraction]);

  const togglePlayPause = useCallback(() => {
    handleFirstInteraction();
    setIsPlaying(prev => !prev);
  }, [handleFirstInteraction]);

  const toggleMute = useCallback(() => {
    handleFirstInteraction();
    setIsMuted(prev => !prev);
  }, [handleFirstInteraction]);

  // Handle series change
  const handleSeriesChange = useCallback((seriesName) => {
    console.log('Changing series from', currentSeries, 'to', seriesName);
    if (seriesName !== currentSeries) {
      setCurrentSeries(seriesName);
      setCurrentIndex(0); // Reset to first video (latest) when changing series
    }
  }, [currentSeries]);

  // Handle speaker navigation (when clicking speaker circle)
  const handleSpeakerFilter = useCallback((speakerName) => {
    if (!speakerName) return;
    
    // Navigate to speaker page with the speaker name directly
    const speakerUrl = `/speaker/${encodeURIComponent(speakerName)}`;
    
    console.log('Navigating to speaker page:', speakerUrl);
    navigate(speakerUrl);
  }, [navigate]);

  // Share functionality
  const handleShare = useCallback(async (shortData) => {
    const shareUrl = generateShareUrl(shortData);
    
    // Update meta tags before sharing to ensure correct preview
    metaTagsService.updateForShort(shortData);
    
    // Log debug info in development
    logSmsPreviewDebug(shortData);
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: shortData.title,
          text: `Watch "${shortData.title}" by ${shortData.speakerName || 'Speaker'} on Penta University`,
          url: shareUrl
        });
      } catch (err) {
        console.log('Share cancelled or failed');
      }
    } else {
      // Desktop fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(shareUrl);
        // Show toast notification (you could add a toast system)
        console.log('Link copied to clipboard!');
        alert('Link copied to clipboard!');
      } catch (err) {
        console.error('Failed to copy link');
      }
    }
  }, []);

  // Handle lecture link navigation
  const handleLectureLink = useCallback((lectureId) => {
    if (!lectureId) return;
    
    console.log('Navigating to lecture:', lectureId);
    
    // Use React Router navigation to the correct lecture route
    navigate(`/lecture/${lectureId}`);
  }, [navigate]);

  // Handle YouTube channel subscribe
  const handleYouTubeSubscribe = useCallback((shortData) => {
    try {
      // For now, use the channel URL from config if available
      // Otherwise use a fallback based on the current short
      if (config?.youtube_channel_url) {
        window.open(config.youtube_channel_url, '_blank');
      } else if (shortData?.shortUrl) {
        // Extract channel from current video and open it
        const videoId = extractVideoId(shortData.shortUrl);
        if (videoId) {
          // Open the video's channel page
          window.open(`https://www.youtube.com/watch?v=${videoId}`, '_blank');
        }
      } else {
        console.warn('No YouTube channel URL available');
      }
    } catch (err) {
      console.error('Error opening YouTube channel:', err);
    }
  }, [config]);

  // Set up auto-advance callback for YouTube player
  useEffect(() => {
    window.shortsAutoAdvance = navigateToNext;
    return () => {
      window.shortsAutoAdvance = null;
    };
  }, [navigateToNext]);
  
  // Handle URL parameter to start with specific video
  useEffect(() => {
    if (shorts.length > 0) {
      const urlParams = new URLSearchParams(location.search);
      const videoParam = urlParams.get('v');
      
      if (videoParam) {
        const decodedParam = decodeURIComponent(videoParam);
        
        // Handle both video IDs and full URLs for backward compatibility
        let videoIdToFind = decodedParam;
        
        // Check if it's a full URL (contains 'youtube.com' or 'youtu.be')
        if (decodedParam.includes('youtube.com') || decodedParam.includes('youtu.be')) {
          // Extract video ID from the URL
          videoIdToFind = extractVideoId(decodedParam);
          console.log('Extracted video ID from URL:', videoIdToFind);
        }
        
        // Find the short that matches either by video ID or full URL
        const foundIndex = shorts.findIndex(short => {
          const shortVideoId = extractVideoId(short.shortUrl);
          // Match by video ID or by full URL (for backward compatibility)
          return shortVideoId === videoIdToFind || short.shortUrl === decodedParam;
        });
        
        if (foundIndex !== -1) {
          console.log('Found matching short at index:', foundIndex, 'for video ID:', videoIdToFind);
          setCurrentIndex(foundIndex);
          // Update meta tags for the specific short
          metaTagsService.updateForShort(shorts[foundIndex]);
        } else {
          console.log('No matching short found for video ID:', videoIdToFind, 'Starting at index 0');
          setCurrentIndex(0);
          // Update meta tags for the first short
          if (shorts[0]) {
            metaTagsService.updateForShort(shorts[0]);
          }
        }
      } else {
        // No specific video requested, update meta tags for current short
        if (shorts[currentIndex]) {
          metaTagsService.updateForShort(shorts[currentIndex]);
        }
      }
    }
  }, [shorts, location.search]);

  // Ensure currentIndex is valid when shorts array changes
  useEffect(() => {
    if (shorts.length > 0 && currentIndex >= shorts.length) {
      console.log('Current index out of bounds, resetting to 0');
      setCurrentIndex(0);
    }
  }, [shorts.length, currentIndex]);

  // Update meta tags when current short changes
  useEffect(() => {
    const currentShort = shorts[currentIndex];
    if (currentShort) {
      metaTagsService.updateForShort(currentShort);
      // Log debug info in development
      logSmsPreviewDebug(currentShort);
    }
  }, [currentIndex, shorts]);

  // Reset meta tags when component unmounts
  useEffect(() => {
    return () => {
      metaTagsService.resetToDefaults();
    };
  }, []);
  
  // Clear initial load flag after data loads
  useEffect(() => {
    if (!dataLoading && (shorts.length > 0 || dataError)) {
      // Add a small delay to ensure smooth transition
      const timer = setTimeout(() => {
        setIsInitialLoad(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [dataLoading, shorts.length, dataError]);

  // Check if feature is enabled
  if (loading || dataLoading) {
    return (
      <div className="shorts-loading">
        <div className="loading-spinner"></div>
        <p>Loading shorts...</p>
      </div>
    );
  }

  // Check if this is a deep link before redirecting
  const urlParams = new URLSearchParams(location.search);
  const hasVideoParam = urlParams.has('v');
  
  if (!loading && !config?.video_options_enabled && !hasVideoParam) {
    // Feature not enabled and not a deep link - redirect
    navigate('/');
    return null;
  }

  // Only show error if not initial load, loading is complete, and no shorts loaded
  if (!isInitialLoad && !dataLoading && dataError && shorts.length === 0) {
    return (
      <div className="shorts-error">
        <h2>Unable to load shorts</h2>
        <p>{dataError}</p>
        <button onClick={refetch} className="retry-button">
          Try Again
        </button>
      </div>
    );
  }

  // Show loading state during initial load even if we have an error
  if (isInitialLoad || (dataLoading && shorts.length === 0)) {
    return (
      <div className="shorts-loading">
        <div className="loading-spinner"></div>
        <p>Loading shorts...</p>
      </div>
    );
  }
  
  if (shorts.length === 0) {
    return (
      <div className="shorts-empty">
        <h2>No shorts available</h2>
        <p>Check back later for new content!</p>
      </div>
    );
  }

  const currentShort = shorts[currentIndex];
  
  // Add additional check
  if (!currentShort) {
    console.error('No short at index:', currentIndex, 'Total shorts:', shorts.length);
    // Don't show another loading state, just return null briefly
    return null;
  }
  
  // Debug logging
  console.log('ShortsViewer render:', {
    shortsLength: shorts.length,
    currentIndex,
    currentShort,
    loading: dataLoading,
    error: dataError,
    configEnabled: config?.video_options_enabled,
    series: series,
    currentSeries: currentSeries
  });

  // Get theme color from config or theme
  const themeColor = config?.primaryColor || theme?.colors?.primary || '#e74c3c';

  return (
    <div 
      className={`shorts-container ${isLandscape && isMobileDevice() ? 'mobile-landscape' : ''} ${controlsVisible ? 'controls-visible' : 'controls-hidden'}`}
      ref={containerRef} 
      style={{ '--theme-color': themeColor }}
    >
      {/* Swipe detection overlay */}
      <div 
        className="swipe-detection-layer" 
        {...swipeHandlers}
        onClick={(e) => {
          console.log('Swipe layer clicked. Target:', e.target, 'CurrentTarget:', e.currentTarget);
          console.log('Click position:', e.clientX, e.clientY);
          
          // Check if tap is on a control element
          const isControlTap = e.target.closest('.audio-control-wrapper') ||
                              e.target.closest('.audio-control-btn') || 
                              e.target.closest('.video-nav-controls') ||
                              e.target.closest('.video-progress-bar') ||
                              e.target.closest('.shorts-navigation') ||
                              e.target.closest('.shorts-actions') ||
                              e.target.closest('.shorts-info');

          if (isControlTap) {
            console.log('Click on control detected, ignoring');
            return; // Let control handle their own clicks
          }

          // Handle mobile landscape tap-to-show controls
          if (isLandscape && isMobileDevice()) {
            if (!controlsVisible) {
              console.log('Showing controls in landscape mode');
              setControlsVisible(true);
              
              // Clear existing timer
              if (autoHideTimer) {
                clearTimeout(autoHideTimer);
              }
              
              // Auto-hide after 3 seconds
              const timer = setTimeout(() => {
                console.log('Auto-hiding controls after timeout');
                setControlsVisible(false);
              }, 3000);
              setAutoHideTimer(timer);
              
              return; // Don't toggle play/pause when showing controls
            }
          }
          
          console.log('Processing as video click for play/pause');
          // For all other clicks (including video area), handle play/pause
          handleFirstInteraction();
          togglePlayPause();
        }}
      ></div>
      
      {/* Top Navigation - Two-row series system */}
      <ShortsNavigation
        series={series}
        currentSeries={currentSeries}
        shorts={shorts}
        onSeriesChange={handleSeriesChange}
      />

      {/* Main Video Player - Using YouTube API directly for CNN-style volume persistence */}
      <ShortsPlayerYouTube
        key="youtube-player-persistent" // Persistent key to prevent remounting on category change
        shortData={currentShort}
        isPlaying={isPlaying}
        isMuted={isMuted}
        onPlayStateChange={setIsPlaying}
        onMuteStateChange={setIsMuted}
        onNextVideo={navigateToNext}
        onPrevVideo={navigateToPrevious}
        onFirstInteraction={handleFirstInteraction}
      />

      {/* Right Side Actions */}
      <ShortsActions
        speakerName={currentShort?.speakerName}
        likeCount={likeCount}
        hasLiked={hasLiked}
        onSpeakerClick={() => {
          handleControlInteraction();
          handleSpeakerFilter(currentShort?.speakerName);
        }}
        onLikeClick={() => {
          handleControlInteraction();
          toggleLike();
        }}
        onShareClick={() => {
          handleControlInteraction();
          handleShare(currentShort);
        }}
        onYouTubeClick={() => {
          handleControlInteraction();
          handleYouTubeSubscribe(currentShort);
        }}
        onLectureClick={() => {
          handleControlInteraction();
          handleLectureLink(currentShort?.lectureId);
        }}
        hasLectureLink={!!currentShort?.lectureId}
      />

      {/* Bottom Info Overlay */}
      <ShortsInfo
        speakerName={currentShort?.speakerName}
        title={currentShort?.title}
        publishedAt={currentShort?.publishedAt}
      />

      {/* Swipe Indicator removed as requested */}
      
      {/* Swipe feedback indicator */}
      {swipeDirection && (
        <div className={`swipe-feedback swipe-${swipeDirection}`}>
          {swipeDirection === 'up' ? '⬆️' : '⬇️'}
        </div>
      )}
      
      {/* Pause indicator */}
      {!isPlaying && (
        <div className="pause-indicator">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="white" opacity="0.8">
            <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
          </svg>
        </div>
      )}
      
      
      {/* Loading more indicator */}
      {loadingMore && (
        <div className="loading-more-indicator">
          <div className="loading-spinner"></div>
          <p>Loading more shorts...</p>
        </div>
      )}
      
      {/* Mobile Debug Console - Only show in development */}
      {process.env.NODE_ENV === 'development' && (
        <MobileDebugConsole enabled={true} maxLogs={100} />
      )}
    </div>
  );
};

export default ShortsViewer;